import React, { useEffect } from 'react';
import {
	Navigate,
	Route,
	Routes,
	useNavigate,
	useParams,
} from 'react-router-dom';
import {
	RenewalContext,
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import { Exposure, ExposureCreateRequest } from '../../../types/policy';
import AuxDataView from '../../auxData/AuxDataView';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import ProtectedRoute from '../../ProtectedRoute';
import RenewalExposure from '../Renewals/Exposure';
import RenewalPeril from '../Renewals/Peril';
import RenewalPerils from '../Renewals/Perils';

interface Props {
	renewalData: RenewalData;
}

/**
 * Route: '...renewals/:id/exposures/:exposureId'
 * The exposureId can have the format 'added-0, ...'.
 * This is the only renewal component that should set the selectedExposure
 */
const RenewalExposureRoutes = (props: Props) => {
	const { renewalData } = props;
	const navigate = useNavigate();
	const { policy, renewalUpdateObj, selectedExposure, setSelectedExposure } =
		renewalData;
	const params = useParams();
	const { exposureId } = params;
	const currentExposures = policy.exposures;
	const addedExposures = renewalUpdateObj.addExposures;
	const isAdded = exposureId.split('-')[0] === 'added';

	useEffect(() => {
		if (isAdded) {
			const addedIndex = parseInt(exposureId.split('-')[1], 10);
			// type ExposureCreateRequest
			setSelectedExposure(addedExposures[addedIndex]);
		} else {
			// type Exposure
			setSelectedExposure(
				currentExposures.find(
					(exposure: any) => exposure.displayId === exposureId,
				),
			);
		}

		return () => {
			setSelectedExposure(null);
		};
	}, [exposureId, policy.exposures, renewalUpdateObj.addExposures]);

	if (!selectedExposure) {
		// add redirect back to
		return <LargeCenterSpinner />;
	}

	const exposureLocator =
		(selectedExposure as Exposure).locator ||
		(selectedExposure as ExposureCreateRequest).exposureLocator;

	return (
		<RenewalContext.Provider value={renewalData}>
			<Routes>
				<Route
					path='auxData'
					element={
						<ProtectedRoute permission='auxData.fetch'>
							<AuxDataView
								navigate={navigate}
								entityLocator={exposureLocator}
								entityType='exposure'
							/>
						</ProtectedRoute>
					}
				/>
				<Route path='perils'>
					<Route index element={<RenewalPerils />} />
					<Route path=':perilId'>
						<Route index element={<RenewalPeril />} />
						<Route
							path='auxData'
							element={
								<ProtectedRoute permission='auxData.fetch'>
									<AuxDataView
										navigate={navigate}
										entityPath='perilId'
										entityType='peril'
									/>
								</ProtectedRoute>
							}
						/>
					</Route>
				</Route>
				<Route path='' element={<RenewalExposure />} />
				<Route path='/' element={<Navigate to='/' />} />
			</Routes>
		</RenewalContext.Provider>
	);
};

export default withRenewalContext<Props>(RenewalExposureRoutes);
